import React, { useEffect, useState } from 'react';
import { Clock, Calendar } from 'lucide-react';

/**
 * TrainingCountdown
 * Two-phase countdown for the Training cohort:
 *   Phase 1 — Before cart-close: counts down to cart close (urgency: BUY NOW)
 *   Phase 2 — After cart-close, before cohort starts: counts down to first session
 *   Phase 3 — Cohort started: hides itself (returns null)
 *
 * Dates are passed in via props so they're easy to update for future cohorts.
 */
export default function TrainingCountdown({
  cartCloseISO = '2026-07-09T22:59:00Z',   // 23:59 BST = 22:59 UTC
  cohortStartISO = '2026-07-28T18:30:00Z', // 19:30 BST = 18:30 UTC
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const cartClose = new Date(cartCloseISO).getTime();
  const cohortStart = new Date(cohortStartISO).getTime();

  // Choose which target we're counting to
  let target, label, sublabel, urgency;
  if (now < cartClose) {
    target = cartClose;
    label = 'Cart closes in';
    sublabel = 'Last chance to enrol for the July cohort';
    urgency = 'high';
  } else if (now < cohortStart) {
    target = cohortStart;
    label = 'Cohort starts in';
    sublabel = 'Training Portal access opens 48hrs before kick-off';
    urgency = 'medium';
  } else {
    return null;
  }

  const remaining = Math.max(0, target - now);
  const days = Math.floor(remaining / 86_400_000);
  const hours = Math.floor((remaining % 86_400_000) / 3_600_000);
  const minutes = Math.floor((remaining % 3_600_000) / 60_000);
  const seconds = Math.floor((remaining % 60_000) / 1000);

  const accent = urgency === 'high'
    ? { border: 'border-red-600', glow: 'shadow-red-600/30', text: 'text-red-500', dot: 'bg-red-500', icon: Clock }
    : { border: 'border-yellow-400', glow: 'shadow-yellow-400/20', text: 'text-yellow-400', dot: 'bg-yellow-400', icon: Calendar };
  const Icon = accent.icon;

  return (
    <div
      className={`relative bg-gradient-to-r from-black via-gray-950 to-black border-2 ${accent.border} rounded-xl p-4 sm:p-5 mb-6 max-w-3xl shadow-xl ${accent.glow}`}
      data-testid="training-countdown"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2 h-2 rounded-full ${accent.dot} animate-pulse`} aria-hidden="true" />
        <Icon className={`w-4 h-4 ${accent.text}`} aria-hidden="true" />
        <span className={`text-xs font-black uppercase tracking-widest ${accent.text}`}>{label}</span>
      </div>

      <div className="grid grid-cols-4 gap-2 sm:gap-3" data-testid="training-countdown-grid">
        <Unit value={days} label="days" testId="cd-days" accent={accent.text} />
        <Unit value={hours} label="hrs" testId="cd-hours" accent={accent.text} />
        <Unit value={minutes} label="mins" testId="cd-minutes" accent={accent.text} />
        <Unit value={seconds} label="secs" testId="cd-seconds" accent={accent.text} />
      </div>

      <p className="text-xs text-gray-400 mt-3">{sublabel}</p>
    </div>
  );
}

function Unit({ value, label, accent, testId }) {
  const padded = String(value).padStart(2, '0');
  return (
    <div className="bg-gray-950/80 border border-gray-800 rounded-lg p-2.5 sm:p-3 text-center" data-testid={testId}>
      <div className={`text-2xl sm:text-3xl font-black tabular-nums ${accent} leading-none`}>{padded}</div>
      <div className="text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 mt-1">{label}</div>
    </div>
  );
}
